import React, { useEffect, useMemo, useState } from 'react';
import './OnboardingStaff.css';
import { getAllProperties, listUsers, listPropertyAccess, assignPropertyAccess, removePropertyAccess, getAuditTrail } from '../../../services/api';
import { getRoles } from '../../../services/rbac';
import MainHeader from '../../shared/MainHeader/MainHeader';
import { toast } from 'react-toastify';
import { USER_ROLES } from '../../../utils/roleUtils';

const TABS = {
  ACCESS: 'access',
  AUDIT: 'audit'
};

export default function OnboardingStaff() {
  console.log('OnboardingStaff component rendered!');
  const [activeTab, setActiveTab] = useState(TABS.ACCESS);
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);

  // Load real properties list if available; fallback to mock
  useEffect(() => {
    (async () => {
      try {
        setLoadingProps(true);
      const list = await getAllProperties();
        if (Array.isArray(list) && list.length) setProperties(list);
      } catch (_) {
        // keep mock
    } finally {
        setLoadingProps(false);
    }
    })();
  }, []);

  return (
    <>
    <MainHeader />
    <div className="onboarding-container">
      <div className="onboarding-header">
        <div className="onboarding-title">Admin Panel</div>
        <div className="onboarding-actions">
          <button className={`onboarding-tab ${activeTab === TABS.ACCESS ? 'active' : ''}`} onClick={() => setActiveTab(TABS.ACCESS)}>Property Access</button>
          <button className={`onboarding-tab ${activeTab === TABS.AUDIT ? 'active' : ''}`} onClick={() => setActiveTab(TABS.AUDIT)}>Audit Trail</button>
        </div>
      </div>

      <div className="onboarding-content">
        {activeTab === TABS.ACCESS && (
          <AccessManager allProperties={properties} loadingProps={loadingProps} />
        )}
        {activeTab === TABS.AUDIT && (
          <AuditTrail allProperties={properties} />
        )}
      </div>
    </div>
    </>
  );
}

// Removed CreateProperty / API Keys / Import Results from UI per new design

function AccessManager({ allProperties, loadingProps }) {
  const [users, setUsers] = useState([]);
  const [usersCache, setUsersCache] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [roles, setRoles] = useState([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [propertyQuery, setPropertyQuery] = useState('');
  // Table view selector remains single for clarity
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [showAddForm, setShowAddForm] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const items = await getRoles();
        const filtered = (items || []).filter(r => !['super_admin','property-onboarding-staff'].includes(r.name));
        setRoles(filtered);
      } catch (_) {}
    })();
  }, []);

  async function loadUsers() {
    const list = await listUsers(search);
    setUsers(list || []);
    // Merge into cache so previously selected still display in chips
    const next = { ...usersCache };
    (list || []).forEach(u => { next[String(u.id)] = u; });
    setUsersCache(next);
  }

  async function loadAssignments() {
    if (!selectedPropertyId) {
      toast.info('Please select a property first');
      return;
    }
    try {
      const list = await listPropertyAccess(selectedPropertyId);
      setAssignments(Array.isArray(list) ? list : (list?.results || []));
    } catch (err) {
      toast.error(err?.message || 'Failed to load property access');
      setAssignments([]);
    }
  }

  function toggleIdInArray(id, arrSetter) {
    arrSetter(prev => {
      const sid = String(id);
      return prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid];
    });
  }

  async function doAssign(e) {
    e.preventDefault();
    if (!selectedPropertyIds.length || !selectedUserIds.length || !roleId) {
      toast.info('Select at least one property, one user, and a role');
      return;
    }
    try {
      const tasks = [];
      for (const pId of selectedPropertyIds) {
        for (const uId of selectedUserIds) {
          tasks.push(assignPropertyAccess(pId, { userId: Number(uId), roleId: Number(roleId), expiresAt: expiresAt || null }));
        }
      }
      await Promise.all(tasks);
      toast.success(`Assigned access to ${selectedUserIds.length} user(s) across ${selectedPropertyIds.length} propert${selectedPropertyIds.length > 1 ? 'ies' : 'y'}`);
      // if table property matches one of selected, refresh
      if (selectedPropertyId && selectedPropertyIds.includes(String(selectedPropertyId))) {
      await loadAssignments();
      }
      // Clear selections
      setSelectedPropertyIds([]);
      setSelectedUserIds([]);
      setExpiresAt('');
      setSearch('');
      setUsers([]);
    } catch (err) {
      toast.error(err?.message || 'Failed to assign access');
    }
  }

  async function doRemove(id) {
    if (!selectedPropertyId) return;
    try {
      await removePropertyAccess(selectedPropertyId, id);
      toast.success('Access removed');
      await loadAssignments();
    } catch (err) {
      toast.error(err?.message || 'Failed to remove access');
    }
  }

  function onView(a) {
    toast.info(`View access for ${a.first_name} ${a.last_name}`);
  }

  function onEdit(a) {
    toast.info(`Edit access for ${a.first_name} ${a.last_name}`);
  }

  function propertyNameById(id) {
    const p = (allProperties || []).find(x => String(x.id) === String(id));
    return p?.name || `#${id}`;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="onboarding-section-title">Property Access</div>
        <button className="btn-primary" onClick={() => setShowAddForm(!showAddForm)}>{showAddForm ? 'Hide' : 'Add User / Add Access'}</button>
      </div>

      {showAddForm && (
      <div className="onboarding-card" style={{ marginTop: 12 }}>
        <div className="onboarding-section-title">Add Assignment</div>
        <form onSubmit={doAssign}>
            <div className="onboarding-vertical">
              <div className="onboarding-field">
                <div className="onboarding-label">1. Select properties</div>
                <input className="onboarding-input input-property-search" placeholder="Search properties" value={propertyQuery} onChange={e => setPropertyQuery(e.target.value)} />
                <div className="onboarding-list">
                  {(allProperties || [])
                    .filter(p => !propertyQuery || (p.name || '').toLowerCase().includes(propertyQuery.toLowerCase()))
                    .map(p => {
                      const checked = selectedPropertyIds.includes(String(p.id));
                      return (
                        <label key={p.id} className={`onboarding-list-item ${checked ? 'checked' : ''}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleIdInArray(p.id, setSelectedPropertyIds)} />
                          <span>{p.name || `#${p.id}`}</span>
                        </label>
                      );
                    })}
                </div>
                {!!selectedPropertyIds.length && (
                  <div className="onboarding-chips">
                    {selectedPropertyIds.map(id => {
                      const p = (allProperties || []).find(x => String(x.id) === String(id));
                      return <span key={id} className="onboarding-chip">{p?.name || `#${id}`}<button type="button" onClick={() => toggleIdInArray(id, setSelectedPropertyIds)}>×</button></span>;
                    })}
                  </div>
                )}
                {loadingProps && <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>Loading properties…</div>}
              </div>

              <div className="onboarding-field">
                <div className="onboarding-label">2. Find and select users</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input id="searchUsers" name="searchUsers" autoComplete="off" className="onboarding-input" placeholder="Search users" value={search} onChange={e => setSearch(e.target.value)} />
                  <button type="button" className="btn-secondary" onClick={loadUsers}>Search</button>
                </div>
                <div className="onboarding-list" style={{ marginTop: 8 }}>
                  {(users || []).map(u => {
                    const id = String(u.id);
                    const checked = selectedUserIds.includes(id);
                    return (
                      <label key={id} className={`onboarding-list-item ${checked ? 'checked' : ''}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleIdInArray(id, setSelectedUserIds)} />
                        <span>{u.first_name} {u.last_name} ({u.email})</span>
                </label>
                    );
                  })}
                </div>
                {!!selectedUserIds.length && (
                  <div className="onboarding-chips">
                    {selectedUserIds.map(id => {
                      const u = usersCache[String(id)] || users.find(x => String(x.id) === String(id));
                      const label = u ? `${u.first_name} ${u.last_name}` : `#${id}`;
                      return <span key={id} className="onboarding-chip">{label}<button type="button" onClick={() => toggleIdInArray(id, setSelectedUserIds)}>×</button></span>;
                    })}
                  </div>
                )}
              </div>

              <div className="onboarding-field">
                <div className="onboarding-label">3. Assign role and optional expiry</div>
                <div className="onboarding-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <select id="selectRole" name="selectRole" className="onboarding-select" value={roleId} onChange={e => setRoleId(e.target.value)}>
                <option value="">Select role</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.display_name || r.name}</option>
                ))}
              </select>
                  <div style={{ position: 'relative', cursor: 'pointer' }} onClick={(e) => {
                    const input = e.currentTarget.querySelector('input[type="date"]');
                    if (input) {
                      input.focus();
                      if (input.showPicker) {
                        input.showPicker();
                      } else {
                        input.click();
                      }
                    }
                  }}>
                    <input id="expiresAt" name="expiresAt" type="date" autoComplete="off" className="onboarding-input input-date-compact" style={{ cursor: 'pointer' }} value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-primary" type="submit">Assign Access</button>
          </div>
        </form>
      </div>
      )}

      <div className="onboarding-card" style={{ marginTop: 12 }}>
        <div className="onboarding-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Current Access</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="onboarding-select" value={selectedPropertyId} onChange={e => setSelectedPropertyId(e.target.value)}>
              <option value="">Select property</option>
              {(allProperties || []).map(p => (
                <option key={p.id} value={p.id}>{p.name || `#${p.id}`}</option>
              ))}
            </select>
          <button className="btn-secondary" onClick={loadAssignments}>Load</button>
          </div>
        </div>
        <table className="onboarding-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Property</th>
              <th>Role</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 && (
              <tr><td colSpan="7">No assignments</td></tr>
            )}
            {assignments.map(a => (
              <tr key={a.id}>
                <td>{a.first_name} {a.last_name}</td>
                <td>{a.email}</td>
                <td>{selectedPropertyId ? propertyNameById(selectedPropertyId) : '-'}</td>
                <td>{a.display_name || a.role_name}</td>
                <td>{a.expires_at ? new Date(a.expires_at).toISOString().slice(0,10) : '-'}</td>
                <td>{a.is_active ? 'Active' : 'Inactive'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-secondary" onClick={() => onView(a)}>View</button>
                    <button className="btn-secondary" onClick={() => onEdit(a)}>Edit</button>
                    <button className="btn-secondary" onClick={() => doRemove(a.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// API Keys section removed from UI

// Import Results removed from UI

function AuditTrail({ allProperties }) {
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    loadUsers();
    loadAuditTrail();
  }, []);

  async function loadUsers() {
    try {
      const users = await listUsers('');
      setAllUsers(users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }

  async function loadAuditTrail() {
    setLoading(true);
    try {
      const filters = {
        propertyId: propertyFilter !== 'all' ? propertyFilter : undefined,
        userId: userFilter !== 'all' ? userFilter : undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined
      };
      const data = await getAuditTrail(filters);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load audit trail:', err);
      toast.error('Failed to load audit trail');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }

  function applyFilters() {
    loadAuditTrail();
  }

  function formatDateTime(iso) {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  }

  function userNameById(id) {
    const e = entries.find(x => x.user_id === id);
    if (e?.user_name) return e.user_name;
    const u = (allUsers || []).find(x => String(x.id) === String(id));
    return u?.name || `User #${id}`;
  }
  
  function propertyNameById(id) {
    const e = entries.find(x => x.property_id === id);
    if (e?.property_name) return e.property_name;
    const p = (allProperties || []).find(x => String(x.id) === String(id));
    return p?.name || `Property #${id}`;
  }

  return (
    <div>
      <div className="onboarding-section-title">Audit Trail</div>
      
      <div className="onboarding-card" style={{ marginTop: 12 }}>
        <div className="onboarding-toolbar" style={{ flexWrap: 'wrap' }}>
          <select className="onboarding-select" value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)}>
            <option value="all">All properties</option>
            {(allProperties || []).map(p => (
              <option key={p.id} value={p.id}>{p.name || `#${p.id}`}</option>
            ))}
          </select>
          <select className="onboarding-select" value={userFilter} onChange={e => setUserFilter(e.target.value)}>
            <option value="all">All users</option>
            {(allUsers || []).map(u => (
              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
            ))}
          </select>
          <input className="onboarding-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} placeholder="From (YYYY-MM-DD)" />
          <input className="onboarding-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} placeholder="To (YYYY-MM-DD)" />
          <button className="btn-secondary" onClick={applyFilters}>Filter</button>
            </div>
            
        <table className="onboarding-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>User</th>
              <th>Property</th>
              <th>Action</th>
              <th>Details</th>
                            </tr>
                          </thead>
                          <tbody>
            {loading && (
              <tr><td colSpan="5">Loading audit trail...</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan="5">No audit entries found</td></tr>
            )}
            {!loading && entries.map(row => (
              <tr key={row.id}>
                <td>{formatDateTime(row.timestamp)}</td>
                <td>{row.user_name || userNameById(row.user_id)}</td>
                <td>{row.property_name || propertyNameById(row.property_id)}</td>
                <td>{row.action}</td>
                <td>{row.details || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
    </div>
  );
}


